import { PropType } from 'vue';
import { Vue, Options } from 'vue-class-component';
import {
  SceneDTO,
  SceneType,
  SceneObjectType,
  PlantDetailDTO,
  AlarmTrigger,
} from '@/../client-generator/generated-clients/plants';
import { SceneUtils } from '@/core/utils/scene.utils';
import { getSceneObjectIconFromType, isDefaultScene } from '@/core/utils/utils';
import SceneTimeObject from './components/SceneTimeObject/SceneTimeObject.vue';
import ActionSceneSlide from './components/SceneSlides/ActionSceneSlide/ActionSceneSlide.vue';
import TriggerSceneSlide from './components/SceneSlides/TriggerSceneSlide/TriggerSceneSlide.vue';
import ConditionSceneSlide from './components/SceneSlides/ConditionSceneSlide/ConditionSceneSlide.vue';
import { mapState } from 'vuex';
import { SceneAlarmUtils } from '@/core/utils/scene.alarm.utils';
import { SceneButtonUtils } from '@/core/utils/scene.button.utils';

@Options({
  name: 'Scene',
  props: {
    scene: {
      type: Object as PropType<SceneDTO>,
      required: true,
    },
    disableActions: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    SceneTimeObject,
    ActionSceneSlide,
    TriggerSceneSlide,
    ConditionSceneSlide,
  },
  emits: ['edit', 'delete', 'enable', 'disable', 'duplicate'],
  computed: {
    ...mapState(['currentPlant']),
  },
})
export default class Scene extends Vue {
  currentPlant!: PlantDetailDTO;
  scene!: SceneDTO;
  isVisibleDeleteAggregateCommandConfirm = false;
  SceneUtils = SceneUtils;

  get isCustomScene() {
    return !isDefaultScene(this.scene);
  }

  get sceneIcon() {
    switch (this.scene.type) {
      case SceneType.Away:
        return require('@/assets/images/icons/icon_command_exit.svg');
      case SceneType.Home:
        return require('@/assets/images/icons/icon_command_enter.svg');
      case SceneType.BedTime:
        return require('@/assets/images/icons/icon_command_night.svg');
      case SceneType.Wake_Up:
        return require('@/assets/images/icons/icon_command_day.svg');
      default:
        return require('@/assets/images/icons/icon-scene.svg');
    }
  }

  get timeBasedObjectDescriptor(): number {
    return SceneObjectType.DateTime;
  }

  mounted() {
    this.computeIcons();
    this.overrideObjectName();
  }

  handleAction(actionName: string) {
    this.$emit(actionName);
  }

  private computeIcons() {
    this.scene.actions?.forEach((act) => {
      (act as any)['icon'] = getSceneObjectIconFromType(act.sceneObjectType!, act.energyObjectType!);
    });
    this.scene.triggers?.forEach((trg) => {
      (trg as any)['icon'] = getSceneObjectIconFromType(trg.sceneObjectType!, trg.energyObjectType!);
    });
    this.scene.conditions?.forEach((cnd) => {
      (cnd as any)['icon'] = getSceneObjectIconFromType(cnd.sceneObjectType!, cnd.energyObjectType!);
    });
  }

  private overrideObjectName() {
    this.scene.actions?.forEach((act) => {
      switch (act.sceneObjectType) {
        case SceneObjectType.PushNotificationAction:
          act.objectName = this.$t('AppResource.PushNotification');
          break;

        case SceneObjectType.Email:
          act.objectName = this.$t('AppResource.Email');
          break;
        case SceneObjectType.Alarm:
          act.objectName = SceneAlarmUtils.getAlarmSceneObjectName(act);
          break;
        default:
          break;
      }
    });
    this.scene.conditions?.forEach((cond) => {
      switch (cond.sceneObjectType) {
        case SceneObjectType.Weather:
          cond.objectName = this.$t('AppResource.Weather');
          break;
        case SceneObjectType.Alarm:
          cond.objectName = SceneAlarmUtils.getAlarmSceneObjectName(cond);
          break;
        default:
          break;
      }
    });
    this.scene.triggers?.forEach((trg) => {
      switch (trg.sceneObjectType) {
        case SceneObjectType.Weather:
          trg.objectName = this.$t('AppResource.Weather');
          break;
        case SceneObjectType.Alarm:
          trg.objectName = SceneAlarmUtils.getAlarmSceneObjectName(trg);
          break;
        case SceneObjectType.Command:
          trg.objectName = SceneButtonUtils.getButtonTriggerName(trg);
          break;
        default:
          break;
      }
    });
  }
}
